/**
 * WS Product Tabs
 * 1. Tab switching (section-level — not card-level)
 * 2. Swatch colour fallbacks for .ws-card-swatch elements
 * 3. Mobile dot indicators (scroll-sync)
 *
 * Card-level quick-add and variant image swap are now handled
 * by Dawn's native quick-add.js and product-form.js.
 */
(() => {

  // ── Colour name → hex fallback map ──────────────────────────
  const COLOUR_MAP = {
    'black': '#0c0c0c', 'ink': '#0c0c0c',
    'white': '#ffffff', 'ivory': '#f5f0e8', 'cream': '#efe6d0',
    'grey': '#9a9890', 'gray': '#9a9890', 'stone': '#9a9890',
    'light-grey': '#d8d6d0', 'light-gray': '#d8d6d0', 'charcoal': '#3a3a3a', 'slate': '#6a7a8a',
    'sand': '#8b7355', 'khaki': '#8b7355', 'tan': '#c9a96e',
    'brown': '#6b5b4a', 'mocha': '#7a5c4a',
    'forest': '#4a5a3a', 'green': '#4a7a4a', 'olive': '#6b7a3a', 'sage': '#8a9a7a',
    'blue': '#2062fc', 'cobalt': '#1a4fd6', 'cobalt-blue': '#1a4fd6',
    'navy': '#1a2a4a', 'royal-blue': '#2050c0', 'sky': '#6ab0e0', 'sky-blue': '#6ab0e0',
    'teal': '#2a8a7a', 'aqua': '#2ab0b0',
    'red': '#d94f3d', 'crimson': '#c0283a', 'burgundy': '#8a2040',
    'pink': '#f0a0b0', 'blush': '#f2b8c6', 'magenta': '#c93383', 'fuchsia': '#d040a0',
    'purple': '#7b4fa6', 'lavender': '#b8a0d8', 'lilac': '#c8b8e8',
    'yellow': '#f5c842', 'gold': '#d4a030', 'mustard': '#c8902a',
    'orange': '#e87040', 'coral': '#f26b5b', 'peach': '#f5b08a',
  };

  const resolveSwatchColor = (name) => {
    if (!name) return '#ccc';
    const key = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (COLOUR_MAP[key]) return COLOUR_MAP[key];
    const stripped = key.replace(/-/g, '');
    if (COLOUR_MAP[stripped]) return COLOUR_MAP[stripped];
    const firstWord = key.split('-')[0];
    const partial = Object.keys(COLOUR_MAP).find(k => k.includes(firstWord));
    return partial ? COLOUR_MAP[partial] : '#ccc';
  };

  // ── Apply swatch colour fallbacks to .ws-card-swatch elements ──
  const applySwatchFallbacks = (root = document) => {
    root.querySelectorAll('.ws-card-swatch[title]').forEach((swatch) => {
      const computed = window.getComputedStyle(swatch);
      const hasBg = computed.backgroundColor !== 'rgba(0, 0, 0, 0)' && computed.backgroundColor !== 'transparent';
      const hasBgImg = computed.backgroundImage !== 'none';
      if (hasBg || hasBgImg) return;
      swatch.style.backgroundColor = resolveSwatchColor(swatch.title || '');
    });
  };

  // ── Dot sync: updates active dot based on scroll position ──────
  const initDots = (panel) => {
    const dots = Array.from(panel.querySelectorAll('.ws-pt-dot'));
    if (!dots.length) return;

    // On mobile the grid is the horizontal scroller (panel stays block)
    const scroller = panel.querySelector('.ws-pt-grid') || panel;

    const syncDots = () => {
      const cards = Array.from(scroller.querySelectorAll('.card-wrapper.product-card-wrapper, .ws-pt-card'));
      if (!cards.length) return;
      const scrollerLeft = scroller.getBoundingClientRect().left;
      let closestIdx = 0;
      let closestDist = Infinity;
      cards.forEach((card, i) => {
        const dist = Math.abs(card.getBoundingClientRect().left - scrollerLeft);
        if (dist < closestDist) { closestDist = dist; closestIdx = i; }
      });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === closestIdx));
    };

    let scrollTimer;
    scroller.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncDots, 60);
    }, { passive: true });

    syncDots();
  };

  // ── Tab switching ────────────────────────────────────────────
  const initTabs = (section) => {
    const tabs   = section.querySelectorAll('.ws-pt-tab');
    const panels = section.querySelectorAll('.ws-pt-panel');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        panels.forEach(p => p.classList.remove('is-active'));
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        const activePanel = section.querySelector(`.ws-pt-panel[data-panel="${target}"]`);
        if (activePanel) {
          activePanel.classList.add('is-active');
          const scroller = activePanel.querySelector('.ws-pt-grid');
          if (scroller) scroller.scrollLeft = 0;
          initDots(activePanel);
        }
      });
    });

    // Init dots on the initially active panel
    const firstActive = section.querySelector('.ws-pt-panel.is-active');
    if (firstActive) initDots(firstActive);
  };

  // ── Init ─────────────────────────────────────────────────────
  const init = () => {
    document.querySelectorAll('.ws-pt-section').forEach(initTabs);
    applySwatchFallbacks();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.ws-pt-section');
    if (section) {
      initTabs(section);
      applySwatchFallbacks(section);
    }
  });

})();
